import { isConditionGroup, isForeignKeyOnLinkedResource, isRelatedResourceCondition, } from './conditions.js';
/** Scan a schema definition for every property carrying a $ref (single) or items.$ref (array). */
function collectSchemaReferencingProperties(schema) {
    const referencingProperties = [];
    const properties = schema.schema?.properties;
    if (properties && typeof properties === 'object') {
        for (const [foreignKeyPath, property] of Object.entries(properties)) {
            if (property && typeof property === 'object') {
                const schemaProperty = property;
                if (typeof schemaProperty.$ref === 'string') {
                    referencingProperties.push({ foreignKeyPath, ref: schemaProperty.$ref, cardinality: 'one' });
                }
                else {
                    const items = schemaProperty.items;
                    if (items && typeof items === 'object' && typeof items.$ref === 'string') {
                        referencingProperties.push({ foreignKeyPath, ref: items.$ref, cardinality: 'many' });
                    }
                }
            }
        }
    }
    return referencingProperties;
}
/**
 * Resolve a `reach:schemas/<X>` ref to a schema, case-insensitively, preferring
 * id, then plural_name, then name. Live data mixes all three forms with
 * inconsistent casing (`reach:schemas/listing` for a schema named `Listing`).
 */
export function resolveSchemaRef(ref, schemas) {
    const refTarget = ref.split('/').pop()?.trim().toLowerCase();
    if (!refTarget)
        return undefined;
    const byId = schemas.find(s => s.id.toLowerCase() === refTarget);
    if (byId)
        return byId;
    const byPluralName = schemas.find(s => (s.plural_name ?? s.pluralName ?? '').toLowerCase() === refTarget);
    if (byPluralName)
        return byPluralName;
    return schemas.find(s => (s.name ?? '').toLowerCase() === refTarget);
}
/** Build the relationship graph from schema $ref declarations. */
export function collectSchemaRelationships(schemas) {
    const relationships = [];
    for (const sourceSchema of schemas) {
        for (const referencingProperty of collectSchemaReferencingProperties(sourceSchema)) {
            const targetSchema = resolveSchemaRef(referencingProperty.ref, schemas);
            if (targetSchema) {
                const label = getRelationshipLabel(sourceSchema, referencingProperty.foreignKeyPath);
                relationships.push({
                    id: `${sourceSchema.id}:${referencingProperty.foreignKeyPath}`,
                    sourceSchemaId: sourceSchema.id,
                    targetSchemaId: targetSchema.id,
                    foreignKeyPath: referencingProperty.foreignKeyPath,
                    cardinality: referencingProperty.cardinality,
                    label,
                });
            }
        }
    }
    return relationships;
}
/**
 * Relationships usable from a given subject.
 *
 * - At the contact level (subjectSchemaId === contactSchemaId): relationships
 *   whose foreign key is either on the contact or on the linked record.
 * - Inside a related-resource block (depth ≥ 2): relationships touching the
 *   subject schema, EXCLUDING any that touch the contacts schema — the project
 *   explicitly rules out contact→contact traversal through a shared object.
 */
export function getRelationshipOptionsForSubject(args) {
    const { graph, subjectSchemaId } = args;
    const subjectIsContact = subjectSchemaId === graph.contactSchemaId;
    const usableFromSubject = graph.relationships.filter(relationship => {
        const touchesSubject = relationship.sourceSchemaId === subjectSchemaId || relationship.targetSchemaId === subjectSchemaId;
        if (subjectIsContact) {
            return touchesSubject;
        }
        const touchesContact = relationship.sourceSchemaId === graph.contactSchemaId ||
            relationship.targetSchemaId === graph.contactSchemaId;
        return touchesSubject && !touchesContact;
    });
    return usableFromSubject;
}
/**
 * Bind a stored related-resource condition to a relationship by its PERSISTED
 * schemaId + foreignKeyPath + foreignKeyOn — never re-derive from discovery, so
 * every existing segment keeps its exact behavior with nothing to backfill.
 * Returns undefined when the stored value no longer appears in the graph; the
 * builder surfaces those read-only instead of silently re-binding.
 */
export function findRelationshipForStoredCondition(args) {
    const { graph, subjectSchemaId, storedRelatedResource } = args;
    if (isForeignKeyOnLinkedResource(storedRelatedResource)) {
        return graph.relationships.find(relationship => relationship.sourceSchemaId === storedRelatedResource.schemaId &&
            relationship.targetSchemaId === subjectSchemaId &&
            relationship.foreignKeyPath === storedRelatedResource.foreignKeyPath);
    }
    return graph.relationships.find(relationship => relationship.sourceSchemaId === subjectSchemaId &&
        relationship.targetSchemaId === storedRelatedResource.schemaId &&
        relationship.foreignKeyPath === storedRelatedResource.foreignKeyPath);
}
/**
 * Extract field identifier from jsonPath (e.g., ".status" → "status").
 */
export function fieldNameFromJsonPath(jsonPath) {
    if (!jsonPath || typeof jsonPath !== 'string')
        return null;
    // Handle ".fieldName" format
    if (jsonPath.startsWith('.'))
        return jsonPath.slice(1) || null;
    return jsonPath;
}
/** Infer segment builder field type from JSON Schema property. */
function fieldTypeFromSchemaProperty(prop) {
    if (!prop || typeof prop !== 'object')
        return 'unknown';
    const schemaType = prop.type;
    const type = Array.isArray(schemaType)
        ? schemaType.find(candidate => candidate !== 'null')
        : schemaType;
    const format = prop.format;
    if (format === 'date' || format === 'date-time')
        return 'date';
    if (type === 'string')
        return 'string';
    if (type === 'number' || type === 'integer')
        return 'number';
    if (type === 'boolean')
        return 'boolean';
    if (type === 'array')
        return 'array';
    if (prop.$ref !== undefined)
        return 'reference';
    return 'unknown';
}
/** Extract enum values from JSON Schema property; returns strings for dropdown use. */
function enumFromSchemaProperty(prop) {
    if (!prop || typeof prop !== 'object')
        return undefined;
    const enumVal = prop.enum;
    if (!Array.isArray(enumVal) || enumVal.length === 0)
        return undefined;
    return enumVal.map(v => (v === null || v === undefined ? '' : String(v)));
}
/**
 * Get property at jsonPath from schema.properties, walking dotted paths into
 * nested `properties` (e.g. ".status" or ".location.name").
 */
function getSchemaProperty(schema, jsonPath) {
    const pathParts = jsonPath
        .replace(/^\$\.?/, '')
        .replace(/^\./, '')
        .split('.')
        .filter(Boolean);
    let currentSchema = schema.schema;
    for (const pathPart of pathParts) {
        const property = currentSchema?.properties?.[pathPart];
        if (!property || typeof property === 'boolean')
            return undefined;
        currentSchema = property;
    }
    return currentSchema;
}
/**
 * Get field metadata (name, type, label, description) from schema's fields_to_expose.
 * - name: jsonPath-derived value persisted to JSON (condition.field); e.g. ".status" → "status".
 * - label: friendly name for dropdown (fields_to_expose.name when present); falls back to name.
 */
export function getSchemaFieldInfos(schema) {
    const expose = schema.fields_to_expose ?? schema.fieldsToExpose;
    if (!expose || !Array.isArray(expose))
        return [];
    const infos = [];
    for (const f of expose) {
        const pathName = fieldNameFromJsonPath(f.jsonPath);
        if (!pathName)
            continue;
        const prop = getSchemaProperty(schema, f.jsonPath);
        const type = fieldTypeFromSchemaProperty(prop);
        const enumValues = enumFromSchemaProperty(prop);
        const label = f.name && typeof f.name === 'string' ? f.name : pathName;
        infos.push({
            name: pathName,
            type,
            label,
            description: typeof f.description === 'string' ? f.description : undefined,
            ...(enumValues && enumValues.length > 0 && { enum: enumValues }),
        });
    }
    return infos.sort((a, b) => a.name.localeCompare(b.name));
}
function getRelationshipLabel(schema, foreignKeyPath) {
    const fieldLabel = getSchemaFieldInfos(schema).find(field => field.name === foreignKeyPath)?.label;
    return fieldLabel && fieldLabel !== foreignKeyPath
        ? `${fieldLabel} (${foreignKeyPath})`
        : foreignKeyPath;
}
export function buildSchemaRelationshipGraph(schemaDefinitions, contactSchemaId) {
    return {
        contactSchemaId,
        schemas: schemaDefinitions.map(schemaDefinition => ({
            id: schemaDefinition.id,
            name: schemaDefinition.name,
            description: schemaDefinition.description,
            fields: getSchemaFieldInfos(schemaDefinition),
            externalIdField: schemaDefinition.external_id_field ?? schemaDefinition.externalIdField ?? '',
        })),
        relationships: collectSchemaRelationships(schemaDefinitions),
    };
}
/**
 * Return every related-resource binding in `conditionGroups` that the visual
 * builder could NOT resolve against the graph — the exact condition that renders
 * "This relationship is no longer available" and (worse) compiles to a silent
 * zero-recipient predicate. Mirrors the builder's resolution precisely: each
 * binding is checked with `findRelationshipForStoredCondition` relative to its
 * subject (the contact at top level; the parent related resource's schema when
 * nested), so a wrong `foreignKeyOn` direction or a cross-subject binding is
 * rejected. This is the backstop the dry compile does NOT provide — a mis-bound
 * relationship falls back to scalar there instead of throwing.
 */
export function findInvalidRelationshipBindings(args) {
    const { graph, conditionGroups } = args;
    const invalidBindings = [];
    const visit = (items, subjectSchemaId) => {
        for (const item of items) {
            if (isConditionGroup(item)) {
                visit(item.conditions, subjectSchemaId);
                continue;
            }
            if (!isRelatedResourceCondition(item))
                continue;
            const storedRelatedResource = item.relatedResource;
            const resolvable = subjectSchemaId !== null &&
                findRelationshipForStoredCondition({ graph, subjectSchemaId, storedRelatedResource }) !==
                    undefined;
            if (!resolvable) {
                invalidBindings.push({
                    schemaId: storedRelatedResource.schemaId,
                    foreignKeyPath: storedRelatedResource.foreignKeyPath,
                    foreignKeyOn: storedRelatedResource.foreignKeyOn,
                });
            }
            // Descend using the queried resource as the new subject, matching the builder.
            visit(item.conditions, storedRelatedResource.schemaId);
        }
    };
    for (const group of conditionGroups)
        visit(group.conditions, graph.contactSchemaId);
    return invalidBindings;
}
//# sourceMappingURL=segment-schema-graph.js.map